import React, { useState, useEffect } from "react";
import axios from "axios";
import PinIcon from "./PinIcon";
import getSecret from "./getSecret";

function PinModal({
  pin,
  setPin,
  onClose,
  mode,
  onDelete,
  serverValue = "dev",
}) {
  const [dropdown, setDropdown] = useState(pin.dropdown || "");
  const [number, setNumber] = useState(pin.number);
  const [color, setColor] = useState(pin.color);
  const [rotation, setRotation] = useState(pin.rotation);
  const [showDropdownError, setShowDropdownError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [cosmeticProducts, setCosmeticProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingProductName, setEditingProductName] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ product: null, callback: null });
  console.log("pin", pin, "dropdown", dropdown, "number", number, "mode", mode);

  const urlParams = new URLSearchParams(window.location.search);
  const organizationId = urlParams.get('organization_id') || 0;
  const userId = urlParams.get('user_id') || 0;

  const getToken = async () => {
    const secret = await getSecret("salusemr-token");
    return secret.token;
  };

  // Fetch cosmetic products from API
  const getCosmeticProducts = async () => {
    setLoadingProducts(true);
    let url = "https://dev.salusemr.com/api/index?action=get_cosmetic_products";
    if (serverValue === "prod") {
      url = "https://salusemr.com/api/index?action=get_cosmetic_products";
    }

    const Token = await getToken();

    try {
      const response = await axios.post(
        url,
        {
          organization_id: organizationId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      console.log("response", response);

       if (response.data && response.data) {
         // Transform the data structure and filter out soft-deleted items
         const transformedData = response.data.data
           .filter(item => item.Status !== 0) // Exclude soft-deleted items
           .map(item => ({
             title: item.Name, // Change Name to title
             category: item.Organization || "Others - Unit", // Use Organization as category or default
             organization: item.Organization,
             status: item.Status,
             default_data_flag: item.Default_data_flag,
             Cosmetic_id: item.Cosmetic_id
           }));
           console.log("transformedData", transformedData);
         
         setCosmeticProducts(transformedData);
         setFilteredProducts(transformedData);
      } else {
        // Fallback to default data if API fails
        const fallbackData = [
          { title: "Botox", category: "Others - Unit" },
          { title: "Dysport", category: "Others - Unit" },
          { title: "Jeuveau", category: "Others - Unit" },
          { title: "Restylane Defyne", category: "Others - Syringe" },
          { title: "Restylane Lyft", category: "Others - Syringe" },
        ];
        setCosmeticProducts(fallbackData);
        setFilteredProducts(fallbackData);
      }
    } catch (error) {
      console.error("Error fetching cosmetic products:", error);
      // Fallback to default data on error
      const fallbackData = [
        { title: "Botox", category: "Others - Unit" },
        { title: "Dysport", category: "Others - Unit" },
        { title: "Jeuveau", category: "Others - Unit" },
        { title: "Restylane Defyne", category: "Others - Syringe" },
        { title: "Restylane Lyft", category: "Others - Syringe" },
      ];
      setCosmeticProducts(fallbackData);
      setFilteredProducts(fallbackData);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load cosmetic products when modal opens
  useEffect(() => {
    if (mode !== "edit") {
      getCosmeticProducts();
    }
  }, [mode, serverValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDropdown(pin.dropdown || "");
    setNumber(pin.number);
    setColor(pin.color);
    setRotation(pin.rotation);
    setShowDropdownError(false); // Reset error when pin changes
    setSearchTerm(pin.dropdown || ""); // Set search term to current selection
  }, [pin]);

  // Filter products based on search term
  useEffect(() => {
    const filtered = cosmeticProducts.filter((product) =>
      product.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchTerm, cosmeticProducts]);

  useEffect(() => {
    // No longer need canvas preview since we're using PinIcon component
  }, [color, number, rotation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const handleSave = () => {
    // Validate dropdown selection for new pins
    if (mode !== "edit" && (!dropdown || dropdown === "")) {
      setShowDropdownError(true);
      return; // Don't save if no product is selected
    }

    // Validate that unit is not empty
    if (!number || number === "") {
      return; // Don't save if unit is empty
    }

    setShowDropdownError(false); // Clear error on successful save
    setPin({ ...pin, dropdown, number, color, rotation });
  };

  const handleProductSelect = (productTitle) => {
    setDropdown(productTitle);
    setSearchTerm(productTitle);
    setShowDropdownError(false);
    setIsDropdownOpen(false);
  };

  const handleClearSelection = () => {
    setDropdown("");
    setSearchTerm("");
    setShowDropdownError(false);
  };

  // Add new cosmetic product API call
  const addCosmeticProduct = async (productName) => {
    try {
      let url = "https://dev.salusemr.com/api/index?action=add_cosmetic_products";
      if (serverValue === "prod") {
        url = "https://salusemr.com/api/index?action=add_cosmetic_products";
      }

      const Token = await getToken();

      const response = await axios.post(
        url,
        {
          organization_id: organizationId,
          name: productName,
          user_id: userId
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      console.log("Add product response:", response);
      
      if (response.data) {
        // After successful addition, refresh the product list
        await getCosmeticProducts();
        
        // Select the newly added product
        setDropdown(productName);
        setSearchTerm(productName);
        setShowDropdownError(false);
        setIsDropdownOpen(false);
      }
    } catch (error) {
      console.error("Error adding cosmetic product:", error);
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Check if search term exists in filtered products
      const exactMatch = filteredProducts.find(product => 
        product.title.toLowerCase() === searchTerm.toLowerCase()
      );
      
      if (!exactMatch && searchTerm.trim()) {
        // No exact match found, add new product
        await addCosmeticProduct(searchTerm.trim());
      } else if (exactMatch) {
        // Exact match found, select it
        handleProductSelect(exactMatch.title);
      }
    }
  };

  // Edit cosmetic product
  const editCosmeticProduct = async (productId, newName) => {
    console.log("productId", productId, "newName", newName);
    try {
      let url = "https://dev.salusemr.com/api/index?action=update_cosmetic_products";
      if (serverValue === "prod") {
        url = "https://salusemr.com/api/index?action=update_cosmetic_products";
      }

      const Token = await getToken();

      const response = await axios.post(
        url,
        {
          cosmetic_id: productId,
          name: newName,
          user_id: userId
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      console.log("Edit product response:", response);
      
      if (response.data) {
        // After successful edit, refresh the product list
        await getCosmeticProducts();
        setEditingProduct(null);
        setEditingProductName("");
      }
    } catch (error) {
      console.error("Error editing cosmetic product:", error);
    }
  };

  // Delete cosmetic product (soft delete)
  const deleteCosmeticProduct = async (productId) => {
    try {
      let url = "https://dev.salusemr.com/api/index?action=delete_cosmetic_products";
      if (serverValue === "prod") {
        url = "https://salusemr.com/api/index?action=delete_cosmetic_products";
      }

      const Token = await getToken();

      const response = await axios.post(
        url,
        {
          cosmetic_id: productId,
          user_id: userId,
          status: "0"
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      console.log("Delete product response:", response);
      
      if (response.data) {
        // After successful delete, refresh the product list
        await getCosmeticProducts();
        // Reopen dropdown so user can see the product was deleted
        setIsDropdownOpen(true);
      }
    } catch (error) {
      console.error("Error deleting cosmetic product:", error);
    }
  };

  // Handle edit button click
  const handleEditClick = (e, product) => {
    e.stopPropagation();
    if (Number(product?.default_data_flag) === 1) {
      return;
    }
    setEditingProduct(product);
    setEditingProductName(product.title);
  };

  // Handle delete button click
  const handleDeleteClick = (e, product) => {
    e.stopPropagation();
    if (Number(product?.default_data_flag) === 1) {
      return;
    }
    setConfirmData({
      product,
      callback: () => deleteCosmeticProduct(product.Cosmetic_id)
    });
    setShowConfirmModal(true);
  };

  // Handle confirmation modal responses
  const handleConfirmYes = () => {
    if (confirmData.callback) {
      confirmData.callback();
    }
    setShowConfirmModal(false);
    setConfirmData({ product: null, callback: null });
  };

  const handleConfirmNo = () => {
    setShowConfirmModal(false);
    setConfirmData({ product: null, callback: null });
  };

  // Handle edit save
  const handleEditSave = async (e) => {
    e.stopPropagation();
    if (editingProduct && Number(editingProduct?.default_data_flag) === 1) {
      return;
    }
    if (editingProductName.trim()) {
      console.log("editingProduct", editingProduct);
      await editCosmeticProduct(editingProduct.Cosmetic_id , editingProductName.trim());
    }
  };

  // Handle edit cancel
  const handleEditCancel = (e) => {
    e.stopPropagation();
    setEditingProduct(null);
    setEditingProductName("");
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "transparent",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        zIndex: 1000,
      }}
    >
      <div
        className="pin-modal"
        style={{
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 2px 12px rgba(25,118,210,0.12)",
          padding: 16,
          width: mode !== "edit" ? 300 : 200,
          margin: "16px 0 0 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          position: "relative",
        }}
      >
        <h3
          style={{
            textAlign: "center",
            fontSize: "1rem",
            fontWeight: 600,
            marginBottom: 12,
            letterSpacing: 0.01,
            color: "#02414a",
          }}
        >
          {mode === "edit" ? "Edit Pin" : "Add Pin"}
        </h3>
        {mode !== "edit" && (
          <div
            className="dropdown-container"
            style={{ marginBottom: 10, position: "relative" }}
          >
            <label
              style={{
                fontWeight: 500,
                marginBottom: 2,
                display: "block",
                fontSize: 12,
              }}
            >
              Select Product
            </label>
            <div style={{ position: "relative" }}>
               <input
                 type="text"
                 value={searchTerm}
                 onChange={(e) => {
                   setSearchTerm(e.target.value);
                   setDropdown(""); // Clear selection when typing
                   setIsDropdownOpen(true);
                   setShowDropdownError(false);
                 }}
                 onKeyPress={handleSearchKeyPress}
                 onFocus={() => setIsDropdownOpen(true)}
                 placeholder="Search or select a product..."
                style={{
                  width: "100%",
                  fontSize: 14,
                  padding: "8px 35px 8px 12px",
                  borderRadius: 4,
                  border: showDropdownError
                    ? "1px solid #e53935"
                    : "1px solid #e0e0e0",
                  boxSizing: "border-box",
                  height: "36px",
                }}
              />
              {/* Show + icon when no exact match found and organization_id/user_id exist */}
              {searchTerm && 
               organizationId && 
               userId && 
               !filteredProducts.find(product => 
                 product.title.toLowerCase() === searchTerm.toLowerCase()
               ) && (
                <button
                  type="button"
                  onClick={() => addCosmeticProduct(searchTerm.trim())}
                  style={{
                    position: "absolute",
                    right: dropdown ? 35 : 10,
                    top: "38%",
                    transform: "translateY(-50%)",
                    background: "#43a047",
                    border: "none",
                    fontSize: 16,
                    color: "#fff",
                    cursor: "pointer",
                    padding: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#2e7d32";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#43a047";
                  }}
                  title="Add new product"
                >
                  +
                </button>
              )}
              {dropdown && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "38%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: 16,
                    color: "#666",
                    cursor: "pointer",
                    padding: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f0f0f0";
                    e.target.style.color = "#333";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "none";
                    e.target.style.color = "#666";
                  }}
                  title="Clear selection"
                >
                  ✕
                </button>
              )}
            </div>

            {isDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 3px 3px",
                  maxHeight: "150px",
                  overflowY: "auto",
                  zIndex: 1001,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {loadingProducts ? (
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "#666",
                      textAlign: "center",
                    }}
                  >
                    Loading products...
                  </div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <div
                      key={product.title}
                      onClick={() => handleProductSelect(product.title)}
                      style={{
                        padding: "8px 12px",
                        fontSize: 12,
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        ":hover": { background: "#f5f5f5" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        position: "relative"
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.background = "#f5f5f5")
                      }
                      onMouseLeave={(e) => (e.target.style.background = "#fff")}
                    >
                      {editingProduct && editingProduct.title === product.title  ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                          <input
                            type="text"
                            value={editingProductName}
                            onChange={(e) => setEditingProductName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleEditSave(e);
                              } else if (e.key === 'Escape') {
                                handleEditCancel(e);
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "4px 8px",
                              fontSize: "12px",
                              border: "1px solid #ddd",
                              borderRadius: "3px",
                              outline: "none"
                            }}
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            style={{
                              background: "#43a047",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              padding: "4px 8px",
                              fontSize: "10px",
                              cursor: "pointer"
                            }}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            style={{
                              background: "#e53935",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              padding: "4px 8px",
                              fontSize: "10px",
                              cursor: "pointer"
                            }}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                     product.Cosmetic_id  ? 
                     <>
                          <span style={{ flex: 1 }}>{product.title}</span>
                          {Number(product?.default_data_flag) !== 1 && (
                          <div style={{ display: "flex", gap: "4px", opacity: 0.7 }}>
                            <button
                              onClick={(e) => handleEditClick(e, product)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "2px 4px",
                                borderRadius: "2px",
                                fontSize: "10px",
                                color: "#666",
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#e3f2fd";
                                e.target.style.color = "#1976d2";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "none";
                                e.target.style.color = "#666";
                              }}
                              title="Edit product"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(e, product)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "2px 4px",
                                borderRadius: "2px",
                                fontSize: "10px",
                                color: "#666",
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#ffebee";
                                e.target.style.color = "#d32f2f";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "none";
                                e.target.style.color = "#666";
                              }}
                              title="Delete product"
                            >
                              🗑️
                            </button>
                          </div>
                          )}
                        </> : 
                           <>
                          <span style={{ flex: 1 }}>{product.title}</span>
                          </> 
                      )}
                    </div>
                  ))
                ) : (
                   <div
                     style={{
                       padding: "8px 12px",
                       fontSize: 12,
                       color: "#666",
                       fontStyle: "italic",
                       textAlign: "center"
                     }}
                   >
                     No products found<br />
                     <span style={{ fontSize: 10, color: "#888" }}>
                       Press Enter to add "{searchTerm}"
                     </span>
                   </div>
                )}
              </div>
            )}

            {showDropdownError && (
              <div
                style={{
                  color: "#e53935",
                  fontSize: 11,
                  marginTop: 4,
                  fontWeight: 500,
                }}
              >
                Please select a product
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <label
            style={{
              fontWeight: 500,
              marginBottom: 2,
              display: "block",
              fontSize: 12,
            }}
          >
            Unit
          </label>
          <input
            type="number"
            min={1}
            value={number || ""}
            onChange={(e) =>
              setNumber(e.target.value === "" ? "" : Number(e.target.value))
            }
            style={{
              width: "100%",
              fontSize: 12,
              padding: 4,
              borderRadius: 3,
              border: "1px solid #e0e0e0",
            }}
          />
        </div>
        <div
          style={{
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div>
            <label
              style={{
                fontWeight: 500,
                marginBottom: 2,
                display: "block",
                fontSize: 12,
              }}
            >
              Color
            </label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{
                width: 40,
                height: 40,
                border: "none",
                borderRadius: "50%",
                background: "none",
                marginTop: 1,
                cursor: "pointer",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 4,
              background: "#f5f5f5",
              boxShadow: "0 1px 3px rgba(25,118,210,0.07)",
            }}
          >
            <div style={{ transform: `rotate(${rotation}deg)` }}>
              <PinIcon color={color} number={number} />
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label
            style={{
              fontWeight: 500,
              marginBottom: 2,
              display: "block",
              fontSize: 12,
            }}
          >
            Rotation
          </label>
          <input
            type="range"
            min={0}
            max={360}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            style={{ width: "80%" }}
          />
          <span
            style={{
              marginLeft: 6,
              fontSize: 11,
              color: "#02414a",
              fontWeight: 500,
            }}
          >
            {rotation}&deg;
          </span>
        </div>
        <div
          className="modal-actions"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 6,
            marginTop: 10,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "4px 12px",
              background: "#f0f0f0",
              color: "#02414a",
              border: "none",
              borderRadius: 3,
              fontWeight: 500,
              fontSize: 11,
            }}
          >
            Cancel
          </button>
          {mode === "edit" && (
            <button
              onClick={() => onDelete(pin)}
              style={{
                padding: "4px 12px",
                background: "#e53935",
                color: "#fff",
                border: "none",
                borderRadius: 3,
                fontWeight: 500,
                fontSize: 11,
              }}
            >
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            style={{
              padding: "4px 12px",
              background: "#02414a",
              color: "#fff",
              border: "none",
              borderRadius: 3,
              fontWeight: 500,
              fontSize: 11,
            }}
          >
            {mode === "edit" ? "Save" : "Add"}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
            }}
          >
            <h3 style={{ margin: "0 0 15px 0", color: "#333" }}>
              Confirm Delete
            </h3>
            <p style={{ margin: "0 0 20px 0", color: "#666" }}>
              Are you sure you want to delete "{confirmData.product?.title}"?
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={handleConfirmNo}
                style={{
                  padding: "8px 16px",
                  background: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                No
              </button>
              <button
                onClick={handleConfirmYes}
                style={{
                  padding: "8px 16px",
                  background: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PinModal;
